import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Alert,
  Heading,
  BodyText,
  Button,
  Card,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-search';
import {
  Search as SearchIcon,
} from 'lucide-react';
import { useInitiateSearch, useJurisdictions, useSearchResults, useLienSummary } from '../../hooks';
import { useUccSearchAvailability, type UccSearchRequest } from '../../hooks/useUccSearch';
import { uccErrorStatus } from '../../hooks/ucc-response';
import { formatCount, formatDate, formatRatioAsPercent } from '../../utils/display-format';
import {
  optionsFrom,
  SEARCH_LOGIC_TYPE_LABEL,
  UccReadFailure,
  UccTable,
  UccTenantScope,
  type SearchLogicType,
} from './ucc-display';

type UccSearchResultEntry = components['schemas']['UccSearchResultEntry'];

const LOGIC_TYPE_OPTIONS = optionsFrom(SEARCH_LOGIC_TYPE_LABEL);

/**
 * The POST /ucc-searches body, per schema-registry/ucc-search.openapi.yaml:
 * `searchLogicType` (the page sent `logicType`, which the spec does not
 * declare), a two-letter upper-case `jurisdictionCode` (pattern ^[A-Z]{2}$),
 * and `initiatedBy`, which the spec lists as required.
 */
export function buildSearchRequest(input: {
  tenantId: string;
  userId: string;
  debtorName: string;
  jurisdictionCode: string;
  searchLogicType: SearchLogicType;
}): UccSearchRequest {
  return {
    tenantId: input.tenantId,
    debtorName: input.debtorName.trim(),
    jurisdictionCode: input.jurisdictionCode.trim().toUpperCase(),
    searchLogicType: input.searchLogicType,
    initiatedBy: input.userId,
  };
}

function useResultColumns(): ColumnDef<UccSearchResultEntry, unknown>[] {
  return useMemo<ColumnDef<UccSearchResultEntry, unknown>[]>(
    () => [
      { accessorKey: 'filingNumber', header: 'Filing #' },
      { accessorKey: 'debtorName', header: 'Debtor' },
      { accessorKey: 'securedPartyName', header: 'Secured Party' },
      {
        accessorKey: 'filingDate',
        header: 'Filed',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.filingDate)}</span>,
      },
      {
        accessorKey: 'matchConfidence',
        header: 'Match',
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">{formatRatioAsPercent(row.original.matchConfidence)}</span>
        ),
      },
    ],
    [],
  );
}

export function UCCSearchPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const userId = useAuthStore(s => s.user?.id) ?? '';
  const [debtorName, setDebtorName] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState('');
  const [logicType, setLogicType] = useState<SearchLogicType>('exact');
  const [activeSearchId, setActiveSearchId] = useState<string | undefined>();

  const initiateSearch = useInitiateSearch();
  const availability = useUccSearchAvailability(tenantId);
  const jurisdictions = useJurisdictions();
  const results = useSearchResults(activeSearchId);
  const lienSummary = useLienSummary(activeSearchId);
  const columns = useResultColumns();

  const jurisdictionOptions = (jurisdictions.data?.data ?? []).map((j) => ({
    value: j.stateCode,
    label: `${j.stateName} (${j.stateCode})`,
  }));
  // The load-time read answers 404 only when the search path is not served.
  // Until that read has answered, a 404 on POST reads the same way; once the
  // service has answered, a POST 404 is the spec's "Jurisdiction not found".
  const serviceMissing =
    uccErrorStatus(availability.error) === 404 ||
    (!availability.isSuccess && uccErrorStatus(initiateSearch.error) === 404);
  const canSearch = Boolean(tenantId && userId && debtorName.trim() && jurisdictionCode) && !serviceMissing;

  const handleSearch = async () => {
    if (!canSearch) return;
    try {
      const search = await initiateSearch.mutateAsync(
        buildSearchRequest({ tenantId, userId, debtorName, jurisdictionCode, searchLogicType: logicType }),
      );
      setActiveSearchId(search.id);
    } catch {
      // Rendered below from initiateSearch.error.
    }
  };

  const summary = lienSummary.data;

  return (
    <div>
      <PageHeader description="Debtor-name lien search in one jurisdiction" />

      <UccTenantScope tenantId={tenantId} subject="searches" />

      {tenantId && serviceMissing && (
        <Alert type="warning" title="The UCC search service is not available" className="mb-6">
          The search service did not answer this page's requests (HTTP 404). It has no production deployment yet, so
          no search can run from this page.
        </Alert>
      )}

      <Card className="p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <Input
            label="Debtor Name"
            placeholder="Full legal name of the debtor"
            value={debtorName}
            onChange={(e) => setDebtorName(e.target.value)}
            required
          />
          <Select
            label="Jurisdiction"
            options={jurisdictionOptions}
            placeholder={jurisdictions.error ? 'Jurisdictions could not be read' : 'Select a state'}
            value={jurisdictionCode}
            onChange={(e) => setJurisdictionCode(e.target.value)}
            required
          />
          <Select
            label="Search Logic"
            options={LOGIC_TYPE_OPTIONS}
            value={logicType}
            onChange={(e) => setLogicType(e.target.value as SearchLogicType)}
          />
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={initiateSearch.isPending || !canSearch}
          >
            <SearchIcon className="mr-2" size={16} />
            {initiateSearch.isPending ? 'Searching...' : 'Search'}
          </Button>
        </div>
        <BodyText variant="ui" className="text-sm text-tertiary mt-3">
          Enter a debtor name and choose one state to search. Both fields are required.
        </BodyText>

        {initiateSearch.error && !serviceMissing && (
          <div className="mt-4">
            <Alert type="error" title="The search could not be started">
              {initiateSearch.error.message}
            </Alert>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <div className="pb-4 mb-4 border-b border-recessed">
              <Heading level={3} size="card">Search Results</Heading>
            </div>
            {!activeSearchId ? (
              <BodyText variant="ui" className="text-sm text-tertiary">
                Results appear here after a search runs.
              </BodyText>
            ) : (
              <UccTable<UccSearchResultEntry>
                columns={columns}
                rows={results.data?.data ?? []}
                isLoading={results.isLoading}
                error={results.error}
                errorTitle="Could not load search results"
                onRetry={() => results.refetch()}
                empty={{
                  title: 'No filings matched this debtor name',
                  description: 'Try the Similar name or Name variants search logic to widen the match.',
                }}
                getRowId={(row) => row.id}
                className=""
              />
            )}
          </Card>
        </div>

        <div>
          <Card className="p-6">
            <Heading level={3} size="card" className="mb-4">Lien Summary</Heading>
            {!activeSearchId ? (
              <BodyText variant="ui" className="text-tertiary text-sm">Run a search to see the lien summary.</BodyText>
            ) : lienSummary.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : lienSummary.error ? (
              <UccReadFailure title="Could not load the lien summary" error={lienSummary.error} onRetry={() => lienSummary.refetch()} />
            ) : !summary ? (
              <BodyText variant="ui" className="text-tertiary text-sm">The lien summary is not available for this search.</BodyText>
            ) : (
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs font-sans text-tertiary">Total liens found</dt>
                  <dd className="text-lg font-sans font-semibold tabular-nums">{formatCount(summary.totalLiensFound)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-sans text-tertiary">Active liens</dt>
                  <dd className="text-lg font-sans font-semibold tabular-nums">{formatCount(summary.activeLiensCount)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-sans text-tertiary">Prior filings listed</dt>
                  <dd className="text-lg font-sans font-semibold tabular-nums">{formatCount(summary.priorFilings.length)}</dd>
                </div>
              </dl>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
